import { createFileRoute, Link } from "@tanstack/react-router";
import { AccountShell } from "@/components/customer/AccountShell";
import { useCustomerSubscription, subscriptionErrorMessage } from "@/hooks/useCustomerSubscriptions";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar, 
  Package, 
  MapPin, 
  ArrowLeft, 
  ExternalLink,
  CheckCircle2,
  Clock,
  XCircle,
  SkipForward,
  PauseCircle,
  PlayCircle,
  Trash2,
  RefreshCw,
  HelpCircle,
  RefreshCcw
} from "lucide-react";
import { mapDeliveryStatus } from "@/lib/customer/status-mappers";
import { Progress } from "@/components/ui/progress";

import { formatISTDate } from "@/lib/date/ist";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { SkipDeliveryDialog } from "@/components/customer/SkipDeliveryDialog";
import { PauseResumeDialog } from "@/components/customer/PauseResumeDialog";
import { CancellationDialog } from "@/components/customer/CancellationDialog";
import { RenewalConfirmationDialog } from "@/components/customer/RenewalConfirmationDialog";





export const Route = createFileRoute("/_customer/account/subscriptions/$subscriptionId")({
  component: SubscriptionDetailPage,
  head: () => ({
    meta: [
      { title: "Subscription Details | Poonam's Healthy Kitchen" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function SubscriptionDetailPage() {
  const { subscriptionId } = Route.useParams();
  const { data: s, isLoading, isError, error, dataUpdatedAt, refetch } = useCustomerSubscription(subscriptionId);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (dataUpdatedAt) {
      setLastUpdated(new Date(dataUpdatedAt).toLocaleTimeString());
    }
  }, [dataUpdatedAt]);

  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [pauseResumeDialogOpen, setPauseResumeDialogOpen] = useState(false);
  const [cancellationDialogOpen, setCancellationDialogOpen] = useState(false);
  const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);

  const [pauseResumeMode, setPauseResumeMode] = useState<"pause" | "resume">("pause");
  const [selectedLedgerItem, setSelectedLedgerItem] = useState<{ id: string; delivery_date: string } | null>(null);


  const handleOpenSkipDialog = (id: string, date: string) => {
    setSelectedLedgerItem({ id, delivery_date: date });
    setSkipDialogOpen(true);
  };

  const handleOpenPauseResume = (mode: "pause" | "resume") => {
    setPauseResumeMode(mode);
    setPauseResumeDialogOpen(true);
  };



  if (isLoading) {
    return (
      <AccountShell title="Loading details..." description="Fetching your subscription data.">
        <div className="space-y-6">
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </AccountShell>
    );
  }

  if (isError || !s) {
    return (
      <AccountShell title="Error" description="Subscription not found or failed to load.">
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-6 text-sm text-destructive">
          {subscriptionErrorMessage(error)}
        </div>
        <Button asChild variant="outline" className="mt-6">
          <Link to="/account/subscriptions">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to subscriptions
          </Link>
        </Button>
      </AccountShell>
    );
  }

  const upcomingDeliveries = s.ledger.filter(l => l.status === "scheduled");
  const pastDeliveries = s.ledger.filter(l => l.status !== "scheduled");

  // Meal counts derived from the delivery ledger.
  // A skipped meal is retained: it never counts as delivered and never reduces entitlement.
  const purchasedMeals = s.total_meals_allocated;
  const deliveredMeals = s.ledger.filter(l => l.status === "delivered").length;
  const skippedMeals = s.ledger.filter(l => l.status === "skipped").length;
  const mealCounts = {
    purchased: purchasedMeals,
    delivered: deliveredMeals,
    skipped: skippedMeals,
    remaining: Math.max(purchasedMeals - deliveredMeals, 0),
    progressPct: purchasedMeals > 0 ? Math.round((deliveredMeals / purchasedMeals) * 100) : 0,
  };

  return (
    <AccountShell 
      title={s.plan_name} 
      description={`Subscription REF: ${s.originating_order_ref || s.id.slice(0, 8)}`}
    >
      <div className="space-y-8">
        {/* Header Actions */}
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm" className="-ml-2 text-muted-foreground">
            <Link to="/account/subscriptions">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Link>
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {(s.status === "active" || s.status === "completed") && (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-primary border-primary/20 hover:bg-primary/5"
                onClick={() => setRenewalDialogOpen(true)}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Renew Subscription
              </Button>
            )}
            {s.status === "active" && (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-amber-600 border-amber-500/20 hover:bg-amber-500/5"
                onClick={() => handleOpenPauseResume("pause")}
              >
                <PauseCircle className="mr-2 h-4 w-4" />
                Pause Subscription
              </Button>
            )}
            {s.status === "paused" && (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-emerald-600 border-emerald-500/20 hover:bg-emerald-500/5"
                onClick={() => handleOpenPauseResume("resume")}
              >
                <PlayCircle className="mr-2 h-4 w-4" />
                Resume Subscription
              </Button>
            )}

            {s.originating_order_id && (
              <Button asChild variant="outline" size="sm">
                <Link to="/account/orders/$orderId" params={{ orderId: s.originating_order_id }}>
                  <ExternalLink className="mr-2 h-3 w-3" />
                  View Original Order
                </Link>
              </Button>
            )}

            <Button asChild variant="outline" size="sm" className="bg-primary/5 border-primary/20">
              <Link to="/contact">
                <HelpCircle className="mr-2 h-4 w-4" />
                Need Help?
              </Link>
            </Button>
          </div>

        </div>

        {/* Quick Summary Card */}
        <section className="grid gap-6 rounded-2xl border border-border/60 bg-card p-6 md:grid-cols-3">
          <div className="space-y-2">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Status</h3>
            <div className="flex items-center gap-2">
              <SubscriptionStatusBadge status={s.status} />
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Period</h3>
            <div className="flex items-center gap-2 text-sm font-medium">
              <Calendar className="h-4 w-4 text-primary/70" />
              <span>{formatISTDate(s.start_date)} — {formatISTDate(s.end_date)}</span>
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Meals</h3>
            <div className="flex items-center gap-2 text-sm font-medium">
              <Package className="h-4 w-4 text-primary/70" />
              <span>{mealCounts.delivered} / {mealCounts.purchased} Delivered</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Skipped (retained): {mealCounts.skipped} · Remaining: {mealCounts.remaining}
            </p>
          </div>
        </section>

        {/* Refresh Timestamp */}
        {lastUpdated && (
          <div className="flex items-center justify-end gap-2 text-[10px] text-muted-foreground">
            <RefreshCcw className="h-3 w-3" />
            Last updated: {lastUpdated}
          </div>
        )}

        {/* Next Meal Highlight */}
        {s.status === "active" && (
          <section className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-6 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-primary">Next Meal</h3>
                {(() => {
                  const today = new Date().toISOString().split('T')[0];
                  const nextMeal = s.ledger
                    .filter(l => l.status === 'scheduled' && l.delivery_date >= today)
                    .sort((a, b) => a.delivery_date.localeCompare(b.delivery_date))[0];
                  
                  if (!nextMeal) return <p className="mt-2 text-sm text-muted-foreground">No upcoming meals scheduled.</p>;

                    return (
                      <div className="mt-2 space-y-1">
                        <p className="text-lg font-bold">
                          {formatISTDate(nextMeal.delivery_date)}
                          {nextMeal.meal_slot && (
                            <span className="ml-2 text-sm font-medium text-muted-foreground uppercase">
                              · {nextMeal.meal_slot}
                            </span>
                          )}
                        </p>
                        {nextMeal.dish_name && (
                          <p className="text-sm font-semibold text-foreground">
                            {nextMeal.dish_name}
                          </p>
                        )}
                        <p className="text-sm font-medium text-muted-foreground">
                          Status: <span className="text-primary">{mapDeliveryStatus(nextMeal.status)}</span>
                        </p>
                      </div>
                    );
                })()}
              </div>
              <div className="flex-1 max-w-xs">
                <div className="mb-2 flex items-center justify-between text-xs font-medium">
                  <span>Meals Progress</span>
                  <span>{mealCounts.progressPct}%</span>
                </div>
                <Progress value={mealCounts.progressPct} className="h-2" />
                <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
                  <span>Delivered: {mealCounts.delivered}</span>
                  <span>Skipped: {mealCounts.skipped}</span>
                  <span>Remaining: {mealCounts.remaining}</span>
                </div>
              </div>
            </div>
          </section>
        )}

        <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
          <div className="space-y-8">
            {/* Delivery Schedule Section */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold tracking-tight">Delivery Ledger</h2>
                <Badge variant="outline" className="font-normal text-[10px] text-muted-foreground">
                  View Only
                </Badge>
              </div>
              
              <div className="flex items-center gap-3">
                <Button asChild variant="outline" size="sm" className="bg-primary/5 hover:bg-primary/10 border-primary/20">
                  <Link to="/account/subscriptions/$subscriptionId/calendar" params={{ subscriptionId: s.id }}>
                    <Calendar className="mr-2 h-4 w-4" />
                    View Delivery Calendar
                  </Link>
                </Button>
              </div>

              <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-secondary/30 border-b border-border/60 text-muted-foreground text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Meal Slot</th>
                      <th className="px-4 py-3 font-medium">Dish</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium text-right">Action</th>
                    </tr>

                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {s.ledger.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="px-4 py-8 text-center text-muted-foreground italic">
                          No delivery records found for this subscription.
                        </td>
                      </tr>
                    ) : (
                      s.ledger.map((l) => (
                        <tr key={l.id} className="hover:bg-secondary/10 transition-colors">
                          <td className="px-4 py-3 font-medium">
                            {formatISTDate(l.delivery_date)}
                          </td>
                          <td className="px-4 py-3 text-xs font-medium uppercase text-muted-foreground">
                            {l.meal_slot || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">
                            {l.dish_name || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <DeliveryStatusBadge status={l.status} skipReason={l.skip_reason} />
                            {l.status === "skipped" && (
                              <p className="mt-1 text-[10px] text-muted-foreground">
                                {(l.skip_reason || "").includes("no extension")
                                  ? "Skipped only — no extension"
                                  : "Retained — subscription extended"}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {l.status === "scheduled" && s.status === "active" && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 text-xs text-primary hover:text-primary hover:bg-primary/5"
                                onClick={() => handleOpenSkipDialog(l.id, l.delivery_date)}
                              >
                                Skip
                              </Button>
                            )}
                          </td>
                        </tr>

                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          {/* Sidebar / Additional Info */}
          <aside className="space-y-6">
            <section className="rounded-xl border border-border/60 bg-card p-5 space-y-4">
              <h3 className="text-sm font-semibold">Delivery Address</h3>
              <div className="flex gap-3 text-sm text-muted-foreground">
                <MapPin className="h-5 w-5 shrink-0 text-primary/60 mt-0.5" />
                <address className="not-italic leading-relaxed">
                  {[
                    s.delivery_address.address_line1,
                    s.delivery_address.address_line2,
                    s.delivery_address.neighbourhood,
                    s.delivery_address.city,
                    s.delivery_address.pincode
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </address>
              </div>
            </section>

            <div className="rounded-xl bg-primary/5 border border-primary/10 p-5 space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-primary/80">Support</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Need to change your delivery address or have questions about your plan? Contact our support team.
              </p>
            </div>

            {(s.status === "active" || s.status === "paused") && (
              <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-5 space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-destructive/80">Danger Zone</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Cancelling your subscription will stop all future scheduled deliveries permanently.
                </p>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="w-full text-xs"
                  onClick={() => setCancellationDialogOpen(true)}
                >
                  <Trash2 className="mr-2 h-3.5 w-3.5" />
                  Cancel Subscription
                </Button>
              </div>
            )}

          </aside>
        </div>
      </div>

      {selectedLedgerItem && (
        <SkipDeliveryDialog
          isOpen={skipDialogOpen}
          onClose={() => setSkipDialogOpen(false)}
          ledgerItem={selectedLedgerItem}
        />
      )}

      <PauseResumeDialog
        isOpen={pauseResumeDialogOpen}
        onClose={() => setPauseResumeDialogOpen(false)}
        subscriptionId={s.id}
        mode={pauseResumeMode}
      />

      <CancellationDialog
        isOpen={cancellationDialogOpen}
        onClose={() => setCancellationDialogOpen(false)}
        subscriptionId={s.id}
      />

      <RenewalConfirmationDialog
        isOpen={renewalDialogOpen}
        onClose={() => setRenewalDialogOpen(false)}
        subscriptionId={s.id}
        planName={s.plan_name}
      />


      <RenewalConfirmationDialog
        isOpen={renewalDialogOpen}
        onClose={() => setRenewalDialogOpen(false)}
        subscriptionId={s.id}
        planName={s.plan_name}
      />
    </AccountShell>



  );
}


function SubscriptionStatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    active: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    paused: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    cancelled: "bg-muted text-muted-foreground border-border",
    completed: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    expired: "bg-muted text-muted-foreground border-border",
  };

  return (
    <Badge 
      variant="outline" 
      className={cn("capitalize font-semibold px-2.5 py-0.5 text-[11px]", variants[status.toLowerCase()] || "")}
    >
      {status}
    </Badge>
  );
}

function DeliveryStatusBadge({ status, skipReason }: { status: string, skipReason: string | null }) {
  const config: Record<string, { icon: any, label: string, className: string }> = {
    scheduled: { 
      icon: Clock, 
      label: "Pending", 
      className: "text-amber-600 bg-amber-500/10 border-amber-500/20" 
    },
    prepared: { 
      icon: Clock, 
      label: "Prepared", 
      className: "text-blue-600 bg-blue-500/10 border-blue-500/20" 
    },
    dispatched: { 
      icon: SkipForward, 
      label: "Dispatched", 
      className: "text-indigo-600 bg-indigo-500/10 border-indigo-500/20" 
    },
    delivered: { 
      icon: CheckCircle2, 
      label: "Delivered", 
      className: "text-emerald-600 bg-emerald-500/10 border-emerald-500/20" 
    },
    skipped: { 
      icon: SkipForward, 
      label: "Skipped", 
      className: "text-muted-foreground bg-muted border-border" 
    },
    cancelled: { 
      icon: XCircle, 
      label: "Cancelled", 
      className: "text-destructive bg-destructive/10 border-destructive/20" 
    },
    failed: { 
      icon: XCircle, 
      label: "Failed", 
      className: "text-destructive bg-destructive/10 border-destructive/20" 
    }
  };

  const item = config[status.toLowerCase()] || { icon: Clock, label: mapDeliveryStatus(status), className: "" };
  const Icon = item.icon;

  return (
    <div className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium", item.className)}>
      <Icon className="h-3 w-3" />
      <span>{item.label}</span>
    </div>
  );
}
